"use client";

import { ArrowDown, ArrowUp, Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";

import AppImage from "@/components/primitives/Image/Image";
import { useAnalytics } from "@/core/analytics";
import { DisplayAgelessAssessment } from "@/core/api/orval/model";
import { useCountdown } from "@/core/hooks";
import { useTranslation } from "@/core/i18n";
import { useAssessmentStep } from "@/features/assessments/AssessmentStepProvider/AssessmentStepProvider";
import { useAssessmentLogger } from "@/features/assessments/logging/useAssessmentLogger";
import type { CapturePhase } from "@/features/assessments/components/ImageCaptureStep/captureTypes";
import type { UploadFrontalImageForAssessmentFn } from "@/features/assessments/components/ImageCaptureStep/hooks/photoApi";
import { useResetOnCameraError } from "@/features/assessments/components/ImageCaptureStep/hooks/useResetOnCameraError";
import { useStudioUiState } from "@/features/assessments/components/ImageCaptureStep/hooks/useStudioUiState";
import { CameraLoadingSkeleton } from "@/features/assessments/components/ImageCaptureStep/stages/components/CameraLoadingSkeleton";
import { CameraSwitchButton } from "@/features/assessments/components/ImageCaptureStep/stages/components/CameraSwitchButton";
import { CameraSwitchFade } from "@/features/assessments/components/ImageCaptureStep/stages/components/CameraSwitchFade";
import { CaptureCountdownOverlay } from "@/features/assessments/components/ImageCaptureStep/stages/components/CaptureCountdownOverlay";
import { CaptureErrorOverlay } from "@/features/assessments/components/ImageCaptureStep/stages/components/CaptureErrorOverlay";
import { CaptureFaceGuide } from "@/features/assessments/components/ImageCaptureStep/stages/components/CaptureFaceGuide";
import { CaptureFlashOverlay } from "@/features/assessments/components/ImageCaptureStep/stages/components/CaptureFlashOverlay";
import { CaptureFooterControls } from "@/features/assessments/components/ImageCaptureStep/stages/components/CaptureFooterControls";
import { CaptureHeader } from "@/features/assessments/components/ImageCaptureStep/stages/components/CaptureHeader";
import { CaptureIntroOverlay } from "@/features/assessments/components/ImageCaptureStep/stages/components/CaptureIntroOverlay";
import { CaptureShutterButton } from "@/features/assessments/components/ImageCaptureStep/stages/components/CaptureShutterButton";
import { CaptureValidationActions } from "@/features/assessments/components/ImageCaptureStep/stages/components/CaptureValidationActions";
import { CaptureViewportFrame } from "@/features/assessments/components/ImageCaptureStep/stages/components/CaptureViewportFrame";
import { CaptureZoomControls } from "@/features/assessments/components/ImageCaptureStep/stages/components/CaptureZoomControls";
import { WebsiteEmbedCameraStartOverlay } from "@/features/assessments/components/ImageCaptureStep/stages/components/WebsiteEmbedCameraStartOverlay";
import type { FrontCaptureResult } from "@/features/assessments/components/ImageCaptureStep/types";
import {
  uploadFrontalFacialImageForAssessment,
  uploadLashBrowEyeCropForAssessment,
} from "@/features/assessments/components/ImageCaptureStep/hooks/photoApi";
import { useWebsiteEmbedCameraBootstrap } from "@/features/assessments/components/ImageCaptureStep/hooks/useWebsiteEmbedCameraBootstrap";
import { CAPTURE_JPEG_QUALITY, FLASH_DELAY_MS } from "@/features/camera/captureConstants";
import { captureFrame } from "@/features/camera/captureFrame";
import { useCameraSession } from "@/features/camera/useCameraSession";
import { useCaptureFeedback } from "@/features/camera/useCaptureFeedback";
import {
  createEyeRegionCrop,
  createFaceCropWithFallback,
  useFaceGuidance,
} from "@/features/facialRecognition";

type LashBrowCaptureStageProps = {
  assessment: DisplayAgelessAssessment;
  headers: HeadersInit;
  platformId: string;
  onComplete: (result: FrontCaptureResult) => void;
  skipIntro?: boolean;
  uploadFn?: UploadFrontalImageForAssessmentFn;
  introMessage?: string;
};

// Mirrors `FrontCaptureStage`: captures and uploads the shared frontal facial
// image (full-face 3:4 crop) via `uploadFrontalFacialImageForAssessment`. The
// only lash/brow-specific difference is the landscape "lash-brow" guide variant
// (which invites a closer eye-region framing — the enclosing provider relaxes
// the "too-close" face-size gate to match). Lash/brow analysis runs server-side
// against this same frontal image.
export function LashBrowCaptureStage({
  assessment,
  headers,
  platformId,
  onComplete,
  skipIntro = false,
  uploadFn = uploadFrontalFacialImageForAssessment,
  introMessage,
}: LashBrowCaptureStageProps): React.ReactElement {
  const { t } = useTranslation();
  const resolvedIntroMessage = introMessage ?? t("lashBrowSteps.intake.captureIntro");
  const analytics = useAnalytics();
  const { flowType } = useAssessmentStep();
  const logger = useAssessmentLogger(assessment.id, { stage: "lash_brow_capture" });
  const { triggerBeep, triggerShutter } = useCaptureFeedback();

  const [phase, setPhase] = useState<CapturePhase>("requesting");
  const [showFlash, setShowFlash] = useState(false);
  const [showRetinaFlash, setShowRetinaFlash] = useState(false);
  const [photoData, setPhotoData] = useState<string | null>(null);
  const [uploadedImageId, setUploadedImageId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [retakeCount, setRetakeCount] = useState(0);
  const [cameraReady, setCameraReady] = useState(false);
  const hasAutoAdvancedRef = useRef(false);
  // A separate, loosely-cropped eye-region image sent only to lash/brow analysis. Held in a ref so a
  // retry of the frontal upload re-sends the same crop without recomputing it.
  const eyeCropDataRef = useRef<string | null>(null);

  const { videoRef, canvasRef, streamRef, errorKind, facingMode, startCamera, switchCamera } =
    useCameraSession({
      setPhase,
      onCameraError: (error, kind) => {
        logger.error("camera_session_start_failed", { errorKind: kind }, error);
      },
    });
  const { needsEmbedCameraTap, beginEmbedCameraSession } =
    useWebsiteEmbedCameraBootstrap(startCamera);
  const [isSwitching, setIsSwitching] = useState(false);

  const isFrontCamera = facingMode === "user";

  const { countdown, startCountdown, cancelCountdown } = useCountdown({
    onTick: triggerBeep,
    onComplete: () => {
      void handleCapture();
    },
  });

  const setCountdownForGuidance = useCallback(
    (v: number | null) => {
      if (v !== null) {
        return startCountdown(v);
      }

      cancelCountdown();
      return true;
    },
    [startCountdown, cancelCountdown],
  );

  const {
    isMLReady,
    faceStatus,
    lightingQuality,
    lightingAnimationPhase,
    canManualCapture,
    lastLandmarksRef,
    getGuidanceText,
    getGuidanceSubtext,
  } = useFaceGuidance({
    phase,
    countdown,
    setCountdown: setCountdownForGuidance,
    videoRef,
    setPhase,
    onMlDetectionUnavailable: (reason, failurePhase) => {
      logger.warn("face_detection_unavailable_manual_capture_enabled", {
        reason,
        phase: failurePhase,
      });
    },
    onStuckTimeoutFired: (lastStatus, stuckPhase) => {
      logger.warn("face_guidance_stuck_manual_capture_enabled", {
        lastStatus,
        phase: stuckPhase,
      });
    },
  });

  const { ringColor, ringGlowColor, ringLightIntensity, lightingIndicator, isFaceAligned } =
    useStudioUiState({
      phase,
      faceStatus,
      lightingQuality,
      t,
    });

  const runUpload = useCallback(
    async (processed: string) => {
      setIsUploading(true);
      setUploadError(null);
      try {
        const uploadResponse = await uploadFn({
          assessmentId: assessment.id,
          platformId,
          photoDataUrl: processed,
          requestOptions: { headers },
        });

        setUploadedImageId(uploadResponse.imageId);

        // Upload the separate eye-region crop for analysis. A failure here is non-fatal: the backend
        // falls back to the full-face frontal image, so we log and continue rather than blocking capture.
        const eyeCropDataUrl = eyeCropDataRef.current;
        if (eyeCropDataUrl) {
          try {
            await uploadLashBrowEyeCropForAssessment({
              assessmentId: assessment.id,
              platformId,
              frontalFacialImageId: uploadResponse.imageId,
              photoDataUrl: eyeCropDataUrl,
              requestOptions: { headers },
            });
          } catch (error) {
            logger.warn("lash_brow_eye_crop_upload_failed_non_fatal", { retakeCount });
            logger.error("lash_brow_eye_crop_upload_failed_error", undefined, error as Error);
          }
        }

        analytics.trackEvent("assessment_photo_captured", {
          flowType,
          retakeCount,
        });
      } catch (error) {
        logger.error("image_capture_upload_failed", { retakeCount }, error as Error);
        setUploadError(t("assessmentSteps.studioSession.error.uploadFailed"));
      } finally {
        setIsUploading(false);
      }
    },
    [assessment.id, platformId, headers, analytics, flowType, retakeCount, logger, t, uploadFn],
  );

  const handleRetryUpload = useCallback(async () => {
    if (!photoData) return;
    await runUpload(photoData);
  }, [photoData, runUpload]);

  const handleCapture = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
      setShowFlash(false);
      setShowRetinaFlash(false);
      setPhase("positioning");
      return;
    }

    const landmarks = lastLandmarksRef.current;
    setShowFlash(true);
    const needsRetinaFlash = lightingQuality === "too-dark" || lightingQuality === "dim";
    if (needsRetinaFlash) setShowRetinaFlash(true);

    await new Promise((resolve) => setTimeout(resolve, FLASH_DELAY_MS));
    triggerShutter();

    const rawPhoto = captureFrame({
      video,
      canvas,
      quality: CAPTURE_JPEG_QUALITY,
      mirror: isFrontCamera,
    });
    if (!rawPhoto) {
      setShowFlash(false);
      setShowRetinaFlash(false);
      setPhase("positioning");
      return;
    }

    // Single JPEG encode on the capture path: the face crop emits a JPEG and
    // the upload adapter only re-encodes when the image still exceeds the max
    // dimension. No intermediate compress pass.
    let processed = rawPhoto;
    try {
      processed = await createFaceCropWithFallback(rawPhoto, landmarks);
    } catch (error) {
      logger.error("image_capture_crop_compress_failed", undefined, error as Error);
    }

    // A separate, loosely-cropped eye-region image for lash/brow validation and analysis. Only a crop
    // cut from detected eye landmarks is sent; without one the server judges the frontal photo itself.
    try {
      eyeCropDataRef.current = await createEyeRegionCrop(rawPhoto, landmarks);
    } catch (error) {
      logger.error("lash_brow_eye_crop_compute_failed", undefined, error as Error);
      eyeCropDataRef.current = null;
    }

    setPhotoData(processed);
    setShowFlash(false);
    setShowRetinaFlash(false);
    setPhase("preview");

    await runUpload(processed);
  }, [
    videoRef,
    canvasRef,
    lastLandmarksRef,
    lightingQuality,
    isFrontCamera,
    triggerShutter,
    runUpload,
    logger,
  ]);

  const handleAdvance = useCallback(() => {
    if (!photoData || !uploadedImageId) return;

    onComplete({
      photoData,
      uploadedImageId,
    });
  }, [photoData, uploadedImageId, onComplete]);

  const handleManualCapture = () => {
    if (!canManualCapture) return;
    if (phase === "capturing" || countdown !== null) return;
    const didStartCountdown = startCountdown(3);
    if (!didStartCountdown) return;
    triggerBeep();
    setPhase("capturing");
  };

  const handleRetake = () => {
    analytics.trackEvent("assessment_photo_retake_requested", {
      assessmentId: assessment.id,
      flowType,
      photoType: "front",
      source: "front_capture_preview",
    });
    hasAutoAdvancedRef.current = false;
    eyeCropDataRef.current = null;
    setPhotoData(null);
    setUploadedImageId(null);
    setUploadError(null);
    setRetakeCount((c) => c + 1);
    setCameraReady(false);
    setPhase("requesting");
    void startCamera();
  };

  const handleSwitchCamera = async () => {
    cancelCountdown();
    if (phase === "capturing" || phase === "ready") {
      setPhase("positioning");
    }
    setIsSwitching(true);
    setCameraReady(false);
    await switchCamera();
    setIsSwitching(false);
  };

  const isCapturing =
    phase === "positioning" ||
    phase === "ready" ||
    phase === "capturing" ||
    phase === "initializing-ml";
  const showFaceGuide = phase !== "error" && phase !== "captured" && phase !== "preview";
  const showRingLights =
    phase !== "error" && phase !== "captured" && phase !== "initializing-ml" && phase !== "preview";

  const canAdvance = Boolean(photoData && uploadedImageId && !isUploading && !uploadError);

  useEffect(() => {
    if (!canAdvance) return;
    if (!photoData || !uploadedImageId) return;
    if (hasAutoAdvancedRef.current) return;

    hasAutoAdvancedRef.current = true;
    onComplete({
      photoData,
      uploadedImageId,
    });
  }, [canAdvance, onComplete, photoData, uploadedImageId]);

  const handleCancelCountdown = useCallback(() => {
    cancelCountdown();
    setPhase("positioning");
  }, [cancelCountdown, setPhase]);

  const resetOnCameraError = useCallback(() => {
    cancelCountdown();
    setShowFlash(false);
    setShowRetinaFlash(false);
  }, [cancelCountdown]);

  useResetOnCameraError({ errorKind, onReset: resetOnCameraError });

  return (
    <div className="relative flex min-h-dvh w-full flex-1 flex-col overflow-hidden bg-black">
      <CaptureHeader
        liveBadgeText={isMLReady ? t("assessmentSteps.studioSession.header.live") : null}
        title={t("assessmentSteps.studioSession.header.title")}
      />

      <CaptureViewportFrame>
        {needsEmbedCameraTap ? (
          <WebsiteEmbedCameraStartOverlay onStart={beginEmbedCameraSession} />
        ) : null}

        {!cameraReady && !isSwitching && phase !== "error" && !needsEmbedCameraTap && (
          <CameraLoadingSkeleton />
        )}

        <CameraSwitchFade isSwitching={isSwitching} />

        <video
          autoPlay
          muted
          playsInline
          className={`h-full w-full object-cover transition-opacity duration-500 ${isFrontCamera ? "scale-x-[-1]" : ""} ${phase === "error" ? "hidden" : ""} ${cameraReady ? "opacity-100" : "opacity-0"}`}
          ref={videoRef}
          onPlaying={() => setCameraReady(true)}
        />

        {!isFrontCamera && cameraReady && (
          <CaptureZoomControls streamRef={streamRef} videoRef={videoRef} />
        )}

        {showRingLights && (
          <div aria-hidden className="pointer-events-none">
            <motion.div
              animate={{
                opacity: lightingAnimationPhase === "pulsing" ? [0.4, 1, 0.4] : 1,
              }}
              className="absolute"
              initial={{ opacity: 0 }}
              style={{
                inset: `-${24 * ringLightIntensity}px`,
                borderRadius: "32px",
                background: `radial-gradient(ellipse 100% 100% at 50% 50%, transparent 40%, rgba(255,248,235,${0.2 * ringLightIntensity}) 60%, rgba(255,250,240,${0.5 * ringLightIntensity}) 80%, rgba(255,252,245,${0.8 * ringLightIntensity}) 100%)`,
              }}
              transition={
                lightingAnimationPhase === "pulsing"
                  ? { duration: 1.2, repeat: 2, ease: "easeInOut" }
                  : { duration: 0.4 }
              }
            />
            <motion.div
              animate={{
                opacity: lightingAnimationPhase === "pulsing" ? [0.5, 1, 0.5] : 0.95,
              }}
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              style={{
                borderRadius: "24px",
                border: `${6 + 6 * ringLightIntensity}px solid rgba(255,250,240,${0.8 * ringLightIntensity})`,
                boxShadow: `0 0 ${30 * ringLightIntensity}px ${12 * ringLightIntensity}px rgba(255,248,235,${0.6 * ringLightIntensity}), 0 0 ${60 * ringLightIntensity}px ${24 * ringLightIntensity}px rgba(255,250,240,${0.4 * ringLightIntensity}), inset 0 0 ${40 * ringLightIntensity}px ${15 * ringLightIntensity}px rgba(255,252,245,${0.3 * ringLightIntensity})`,
              }}
              transition={
                lightingAnimationPhase === "pulsing"
                  ? { duration: 1.2, repeat: 2, ease: "easeInOut" }
                  : { duration: 0.4 }
              }
            />
          </div>
        )}

        <canvas className="hidden" ref={canvasRef} />

        <AnimatePresence>
          {showFaceGuide && (
            <motion.div
              aria-hidden
              animate={{ opacity: 1 }}
              className="pointer-events-none absolute inset-0 flex items-center justify-center"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <CaptureFaceGuide
                glowColor={ringGlowColor}
                horizontalLineEmphasis={faceStatus === "no-face" ? 0.6 : 0.3}
                isAligned={isFaceAligned}
                ringColor={ringColor}
                variant="lash-brow"
                verticalLineEmphasis={
                  faceStatus === "look-left" || faceStatus === "look-right" ? 0.8 : 0.2
                }
              />
            </motion.div>
          )}
        </AnimatePresence>

        {phase === "error" && (
          <CaptureErrorOverlay
            errorKind={errorKind}
            onRetry={() => startCamera({ setPhaseOnStart: false })}
          />
        )}

        <CaptureCountdownOverlay countdown={countdown} />

        <CaptureFlashOverlay showFlash={showFlash} showRetinaFlash={showRetinaFlash} />

        <AnimatePresence>
          {phase === "preview" && photoData && (
            <motion.div
              animate={{ opacity: 1 }}
              className="absolute inset-0 z-20"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
            >
              <div className="relative flex h-full w-full items-center justify-center bg-black">
                <AppImage
                  fill
                  alt={t("assessmentSteps.studioSession.captureAlt")}
                  className="object-contain"
                  sizes="(max-width: 640px) 100vw, 480px"
                  src={photoData}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CaptureViewportFrame>

      <div className="pb-safe relative z-10 w-full bg-black px-4 pt-3 pb-4 sm:px-6">
        <AnimatePresence mode="wait">
          {phase === "preview" && (
            <motion.div
              animate={{ opacity: 1 }}
              className="flex flex-col items-center gap-3"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              key="preview"
              transition={{ duration: 0.25 }}
            >
              {isUploading && <Loader2 className="h-6 w-6 animate-spin text-white/70" />}
              <CaptureValidationActions
                canAdvance={canAdvance}
                errorMessage={uploadError ?? undefined}
                isBlocked={Boolean(uploadError)}
                onAdvance={handleAdvance}
                onRetake={handleRetake}
                onRetryUpload={handleRetryUpload}
              />
            </motion.div>
          )}

          {isCapturing && phase !== "capturing" && (
            <motion.div
              animate={{ opacity: 1 }}
              className="flex flex-col items-center gap-3 text-center"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              key="guidance"
              transition={{ duration: 0.25 }}
            >
              <div>
                <p className="type-heading-sm font-light text-white/90">{getGuidanceText()}</p>
                {(phase === "positioning" || phase === "ready") && (
                  <div className="mt-1">
                    <p className="type-caption text-white/50">{getGuidanceSubtext() ?? ""}</p>
                  </div>
                )}
              </div>

              {(phase === "positioning" || phase === "ready") && isMLReady && (
                <div className="flex items-center justify-center gap-4">
                  <div
                    className={`type-caption flex items-center gap-1.5 ${isFaceAligned ? "text-green-400" : "text-white/40"}`}
                  >
                    <div
                      className={`h-1.5 w-1.5 rounded-full ${isFaceAligned ? "bg-green-400" : "bg-white/40"}`}
                    />
                    <span>{t("assessmentSteps.studioSession.guidance.faceAligned")}</span>
                  </div>
                  <div
                    className={`type-caption flex items-center gap-1.5 ${lightingIndicator.color}`}
                  >
                    {lightingIndicator.icon === "up" && <ArrowUp className="h-3 w-3" />}
                    {lightingIndicator.icon === "down" && <ArrowDown className="h-3 w-3" />}
                    {lightingIndicator.icon === "check" && (
                      <div className="bg-accent h-1.5 w-1.5 rounded-full" />
                    )}
                    <span>{lightingIndicator.text}</span>
                  </div>
                </div>
              )}

              <CaptureFooterControls
                onSwitchCamera={handleSwitchCamera}
                shutter={
                  <CaptureShutterButton
                    boxShadow={`0 0 0 4px ${ringColor}, 0 0 20px ${ringGlowColor}`}
                    disabled={!canManualCapture || countdown !== null}
                    onCapture={handleManualCapture}
                  />
                }
              />
            </motion.div>
          )}

          {phase === "capturing" && (
            <motion.div
              animate={{ opacity: 1 }}
              className="flex flex-col items-center gap-3"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              key="capturing"
              transition={{ duration: 0.2 }}
            >
              <button
                className="type-body-sm text-white/60 active:text-white/90"
                type="button"
                onClick={handleCancelCountdown}
              >
                {t("assessmentSteps.studioSession.cancelCountdown")}
              </button>
              <div className="flex w-full items-center justify-center">
                <div className="w-12 shrink-0" />
                <div className="flex-1" />
                <div className="flex w-12 shrink-0 justify-center">
                  <CameraSwitchButton onSwitch={handleSwitchCamera} />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {!skipIntro && <CaptureIntroOverlay message={resolvedIntroMessage} />}
    </div>
  );
}

// Approximate the encoded byte size of a base64 data URL for upload-size
// telemetry (every 4 base64 chars encode 3 bytes; padding is negligible here).
function estimateDataUrlBytes(dataUrl: string): number {
  const base64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
  return Math.round((base64.length * 3) / 4);
}
